import React from "react";
import { Link } from "react-router-dom";
import Alert from "web-ui/components/internal/Alert";
import Button from "web-ui/components/internal/Button";
import Drawer from "web-ui/components/internal/Drawer";
import Icon, { iconFactory } from "web-ui/components/internal/Icon";
import Table from "web-ui/components/internal/Table";
import TextInput from "web-ui/components/internal/TextInput";

import { type VoiceListVoice } from "../../api/generalTts";
import { getTtsGenerationAudio } from "../../api/ttsGenerationBackend";

import AccordionPanel from "./AccordionPanel";
import styles from "./VoiceDrawer.module.scss";
import {
  formatCharacter,
  formatLanguage,
  formatProvider,
  titleCaseFormatter,
} from "./VoiceFinder";

interface Props {
  className?: string;
  open?: boolean;
  onRequestClose?: () => void;
  voice?: VoiceListVoice;
}

const VoiceDrawer = ({ className, open, onRequestClose, voice }: Props) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [previewText, setPreviewText] = React.useState("I am a hot dog");

  const isUnavailable = voice?.status === "UNAVAILABLE";

  const createEvaluationParams = React.useMemo(() => {
    if (!voice) {
      return undefined;
    }
    const params = new URLSearchParams();
    params.set("languageId", voice.languageId);
    params.set("provider", voice.provider);
    params.set("voiceName", voice.name);
    return params;
  }, [voice]);

  const handlePlay = async () => {
    if (!voice) {
      return;
    }
    setIsPlaying(true);
    try {
      const mp3Data = await getTtsGenerationAudio({
        languageId: voice.languageId,
        text: previewText,
        voiceName: voice.name,
      });
      const url = URL.createObjectURL(mp3Data);
      const audio = new Audio();
      audio.src = url;
      audio.addEventListener("ended", () => URL.revokeObjectURL(url));
      await audio.play();
    } finally {
      setIsPlaying(false);
    }
  };

  return (
    <Drawer
      className={className}
      headerText={voice?.name}
      modal={false}
      onRequestClose={onRequestClose}
      open={open}
    >
      {isUnavailable ? (
        <Alert
          className={styles.alert}
          color="amber"
          description="Voice is marked unavailable by provider"
          leadingVisual={iconFactory("warning")}
        />
      ) : null}
      <div className={styles.panels}>
        <AccordionPanel header="Preview audio">
          <div className={styles.preview}>
            <Button
              isIconOnly={true}
              onClick={handlePlay}
              size="s"
              state={
                isPlaying
                  ? "loading"
                  : !isUnavailable && previewText
                    ? "enabled"
                    : "disabled"
              }
            >
              <Icon color="inherit" name="play" size="s" variant="inverse" />
            </Button>
            <TextInput
              className={styles.input}
              onChange={e => setPreviewText(e.value)}
              placeholder="Add text to preview the voice"
              size="s"
              value={previewText}
            />
          </div>
        </AccordionPanel>
        {voice === undefined ? null : (
          <AccordionPanel header="About">
            <Table className={styles.table} outerBorder={false} size="s">
              <tbody>
                <tr>
                  <td>Voice name</td>
                  <td className={styles["table-value"]}>{voice.name}</td>
                </tr>
                <tr>
                  <td>Provider</td>
                  <td className={styles["table-value"]}>
                    {formatProvider(voice.provider)}
                  </td>
                </tr>
                <tr>
                  <td>Language</td>
                  <td className={styles["table-value"]}>
                    {formatLanguage(voice.languageId)}
                  </td>
                </tr>
                <tr>
                  <td>Multilingual</td>
                  <td className={styles["table-value"]}>
                    {voice.isMultilingual ? "Yes" : "No"}
                  </td>
                </tr>
                <tr>
                  <td>Gender</td>
                  <td className={styles["table-value"]}>
                    {voice.gender === undefined
                      ? "N/A"
                      : titleCaseFormatter(voice.gender)}
                  </td>
                </tr>
                <tr>
                  <td>Character</td>
                  <td className={styles["table-value"]}>
                    {(voice.character ?? "CHARACTER_UNKNOWN") ===
                    "CHARACTER_UNKNOWN"
                      ? "N/A"
                      : formatCharacter(voice.character ?? "CHARACTER_UNKNOWN")}
                  </td>
                </tr>
                <tr>
                  <td>Age</td>
                  <td className={styles["table-value"]}>
                    {voice.age === undefined
                      ? "N/A"
                      : titleCaseFormatter(voice.age)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </AccordionPanel>
        )}
        <AccordionPanel header="Quality">
          <span className={styles.text}>
            This section hasn&apos;t been implemented yet
          </span>
        </AccordionPanel>
        <AccordionPanel header="Evaluations">
          <Link
            className={styles.link}
            to={`https://tts-evaluation.duolingo.com/admin/create-evaluation-v2?${createEvaluationParams?.toString()}`}
          >
            Create new
          </Link>
        </AccordionPanel>
        {voice === undefined ? null : (
          <AccordionPanel header="History">
            <Table className={styles.table} outerBorder={false} size="s">
              <tbody>
                {isUnavailable ? (
                  <tr>
                    <td>Marked unavailable</td>
                    <td className={styles["table-date"]}>N/A</td>
                  </tr>
                ) : null}
                <tr>
                  <td>Voice added</td>
                  <td className={styles["table-date"]}>
                    {voice.addedAt?.toDateString() ?? "N/A"}
                  </td>
                </tr>
              </tbody>
            </Table>
          </AccordionPanel>
        )}
      </div>
    </Drawer>
  );
};

export default VoiceDrawer;
